import React from 'react';
import {
  Avatar,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { getNexusTokens } from '../../theme/tokens';

/**
 * KPI / summary metric card (Phase 7.2).
 * variant: compact (Analytics) | featured (Dashboard) | centered (Reports)
 * static: disables hover lift (recommended for metric grids)
 */
export default function MetricCard({
  title,
  value,
  subtitle,
  icon,
  accentColor,
  trend,
  loading = false,
  variant = 'compact',
  static: isStatic = true,
  sx,
}) {
  const theme = useTheme();
  const tokens = getNexusTokens(theme);
  const accent = accentColor || tokens.colors.accent.primary;

  const cardSx = {
    background: tokens.colors.gradient.surface,
    border: `1px solid ${tokens.colors.border.subtle}`,
    borderRadius: `${tokens.radius.lg}px`,
    boxShadow: theme.shadows[1],
    width: '100%',
    '&.MuiCard-root:hover': isStatic
      ? {
          transform: 'none',
          borderColor: tokens.colors.border.subtle,
          boxShadow: theme.shadows[1],
        }
      : undefined,
    ...(variant === 'featured' && {
      minHeight: 200,
      minWidth: 260,
      height: 220,
      display: 'flex',
      flexDirection: 'column',
    }),
    ...(!isStatic && {
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      '&:hover': {
        borderColor: accent,
        transform: 'translateY(-2px)',
        boxShadow: theme.shadows[4],
      },
    }),
    ...sx,
  };

  if (variant === 'centered') {
    return (
      <Card sx={cardSx}>
        <CardContent sx={{ textAlign: 'center', p: tokens.spacing.cardPadding / 8 }}>
          <Typography
            sx={{
              ...tokens.typography.metricValue,
              color: accent,
              mb: 1,
              fontSize: '2rem',
            }}
          >
            {loading ? <CircularProgress size={28} sx={{ color: accent }} /> : value}
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: tokens.colors.text.muted }}
          >
            {title}
          </Typography>
          {subtitle ? (
            <Typography variant="caption" sx={{ color: tokens.colors.text.secondary, mt: 0.5, display: 'block' }}>
              {subtitle}
            </Typography>
          ) : null}
        </CardContent>
      </Card>
    );
  }

  if (variant === 'featured') {
    return (
      <Card sx={cardSx}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-start',
            pt: 2,
            px: 2.5,
            pb: 0,
            gap: 1.5,
          }}
        >
          {icon ? (
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: 3,
                backgroundColor: accent,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                boxShadow: `0 4px 12px ${accent}40`,
                '& .MuiSvgIcon-root': { fontSize: 28 },
              }}
            >
              {icon}
            </Box>
          ) : null}
          {trend ? (
            <Chip
              label={trend}
              size="small"
              sx={{
                backgroundColor: trend.startsWith('+')
                  ? tokens.colors.accent.success
                  : tokens.colors.accent.error,
                color: 'white',
                fontSize: '0.8rem',
                fontWeight: 600,
              }}
            />
          ) : null}
        </Box>
        <Box
          sx={{
            display: 'flex',
            flex: 1,
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 2.5,
            pb: 2.5,
            pt: 1,
          }}
        >
          <Typography sx={{ ...tokens.typography.metricValueFeatured, color: tokens.colors.text.primary, minWidth: 60 }}>
            {loading ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={32} sx={{ color: accent }} />
                ...
              </Box>
            ) : (
              value
            )}
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', pl: 2, flex: 1 }}>
            <Typography
              variant="h6"
              sx={{
                color: tokens.colors.text.secondary,
                fontWeight: 700,
                fontSize: '1.3rem',
                mb: 0.5,
              }}
            >
              {title}
            </Typography>
            {subtitle ? (
              <Typography variant="body2" sx={{ color: tokens.colors.text.disabled, fontWeight: 500 }}>
                {subtitle}
              </Typography>
            ) : null}
          </Box>
        </Box>
      </Card>
    );
  }

  // compact (default)
  return (
    <Card sx={cardSx}>
      <CardContent sx={{ p: 3 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box>
            <Typography
              sx={{
                ...tokens.typography.metricLabel,
                color: tokens.colors.text.secondary,
                mb: 1,
              }}
            >
              {title}
            </Typography>
            <Typography
              sx={{
                ...tokens.typography.metricValue,
                color: tokens.colors.text.primary,
              }}
            >
              {loading ? (
                <CircularProgress size={28} sx={{ color: accent }} />
              ) : (
                value
              )}
            </Typography>
            {subtitle ? (
              <Typography variant="caption" sx={{ color: tokens.colors.text.muted, mt: 0.5, display: 'block' }}>
                {subtitle}
              </Typography>
            ) : null}
          </Box>
          {icon ? (
            <Avatar
              sx={{
                bgcolor: accent,
                width: 56,
                height: 56,
                boxShadow: `0 4px 12px ${accent}4D`,
                '& .MuiSvgIcon-root': { fontSize: 28 },
              }}
            >
              {icon}
            </Avatar>
          ) : null}
        </Box>
      </CardContent>
    </Card>
  );
}
